import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "../components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  ArrowRight,
  Award,
  BedDouble,
  ChevronRight,
  Clock,
  Dumbbell,
  GraduationCap,
  MessageCircle,
  Phone,
  Minus,
  Monitor,
  Plus,
  Sparkles,
  Smile,
  Users,
  CircleDot,
  ScanFace,
} from "lucide-react";

const PAGE_URL = "https://drvickyjain.com/dimple-creation-surgery-mumbai";

const DIMPLEPLASTY_FAQS = [
  {
    question: "How long does dimple creation surgery take?",
    answer:
      "The procedure takes 20 to 40 minutes under local anesthesia. No hospital admission is required. Most patients leave the clinic within an hour of arrival.",
  },
  {
    question: "Will my dimples look natural?",
    answer:
      "Yes, when placement is based on your facial proportions and smile dynamics. In the first 2 to 4 weeks, the dimple is visible even at rest due to healing. After this period, it appears only when you smile, which is how natural dimples behave.",
  },
  {
    question: "Is dimpleplasty permanent?",
    answer:
      "Yes. The adhesion created between the skin and underlying muscle is permanent. In the early post-operative period, if a patient changes their mind, reversal is possible. Once fully healed, the result is long-lasting.",
  },
  {
    question: "Are there visible scars after dimple creation surgery?",
    answer:
      "No. The incision is made inside the cheek against the inner mucosal surface. There are no external incisions and no visible scars.",
  },
  {
    question: "Is the procedure painful?",
    answer:
      "Local anesthesia is administered before the procedure, so there is no pain during surgery. Mild soreness inside the cheek for 2 to 3 days afterward is normal and managed with standard medication.",
  },
  {
    question: "Can I choose the position of my dimples?",
    answer:
      "Yes. Dr. Jain marks the position with you before surgery, assessing placement based on your facial proportions and how your cheeks move when you smile. The final position is confirmed with you before any incision is made.",
  },
  {
    question: "Can dimpleplasty be combined with other procedures?",
    answer:
      "Yes. It is commonly combined with rhinoplasty, chin augmentation, lip augmentation, and fat grafting for patients who want multiple facial enhancements in a single session.",
  },
  {
    question: "Who should avoid dimple creation surgery?",
    answer:
      "Patients with active oral infections, poor wound healing from systemic conditions, or unrealistic expectations about the degree of change the procedure produces. Dr. Jain assesses suitability during consultation and will advise against the procedure if your anatomy or health history makes it inadvisable.",
  },
];

const dimpleplastyStructuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "MedicalProcedure",
      name: "Dimple creation surgery (dimpleplasty)",
      alternateName: ["Dimpleplasty", "Cheek dimple surgery", "Chin dimple creation"],
      description:
        "Minimally invasive outpatient cosmetic procedure to create natural-looking cheek or chin dimples via an intraoral approach under local anesthesia. Typically 20–40 minutes in a clinic setting with no external scars.",
      procedureType: "PercutaneousProcedure",
      bodyLocation: {
        "@type": "AnatomicalStructure",
        name: "Cheek",
      },
      followup:
        "Days 1–3: mild swelling; dimple may show at rest temporarily. Days 5–7: swelling reduces; dimple more natural with smiling. Weeks 1–2: full activity; oral hygiene important. Weeks 3–4: final dynamic result.",
      howPerformed:
        "Local anesthesia; marks placed while patient smiles; small intraoral incision on buccal mucosa; suture creates adhesion between inner cheek skin and muscle; no external stitches; 20–40 minutes; same-day discharge.",
      url: PAGE_URL,
      additionalProperty: [
        { "@type": "PropertyValue", name: "Invasiveness", value: "Minimally invasive" },
        { "@type": "PropertyValue", name: "Anesthesia", value: "Local anesthesia" },
        { "@type": "PropertyValue", name: "Care setting", value: "Outpatient" },
        { "@type": "PropertyValue", name: "Body areas", value: "Cheek, chin (face)" },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: DIMPLEPLASTY_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    },
  ],
};

function DimpleplastyJsonLd() {
  return (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(dimpleplastyStructuredData) }} />
  );
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Cosmetic Surgery", href: "/#services" },
    { name: "Face", href: "/#services" },
    { name: "Dimple Creation", href: "/dimple-creation-surgery-mumbai" },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Cosmetic Surgery · Face</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-5">
              Dimple Creation Surgery
            </h1>
            <div className="w-12 h-[2px] bg-gold mb-5" />
            <p className="text-lg text-deep-navy/70 mb-8">
              Dimpleplasty by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
            </p>
            <div className="flex flex-wrap gap-3">
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-medium text-premium-purple">Board Certified</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-medium text-premium-purple">17+ Years Experience</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-medium text-premium-purple">International Training</span>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder
              procedureName="Dimple Creation"
              icon="☺"
              size="hero"
              imageSrc={HERO.dimpleplasty}
              imageAlt="Dimple creation surgery"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatIsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="what-is-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">What is Dimple Creation Surgery?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Dimple creation surgery, clinically called dimpleplasty, is a minor cosmetic procedure that creates
              natural-looking dimples on the cheeks or chin. The procedure takes 20 to 40 minutes under local anesthesia,
              requires no external incisions, and leaves no visible scars. Most patients return to normal activity the same
              day.
            </p>
            <p>
              Natural dimples form because of a variation in the zygomaticus major muscle, where the muscle creates a small
              depression in the overlying skin during movement. Dimpleplasty replicates this anatomy by creating a controlled
              adhesion between the inner cheek and the underlying muscle. The result is a dimple that appears when you smile
              and, once fully healed, behaves the same way a natural dimple does.
            </p>
            <p>
              Dr. Vicky Jain performs dimpleplasty for patients who want a subtle enhancement to their smile and
              facial expressions. Dimple placement is planned based on your facial proportions and smile dynamics so the
              result looks like it has always been there.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Surgical Approach</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">
              Dr. Jain&apos;s Approach to Dimpleplasty
            </h2>
            <div className="space-y-6 text-white/75 leading-relaxed">
              <p>
                Placement is the most important decision in dimple creation surgery. A dimple in the wrong position looks
                artificial regardless of how well the procedure is executed. Dr. Jain marks the dimple location with the
                patient smiling and assesses how the cheek moves during different expressions before making any incision.
                The standard placement sits at the intersection of a vertical line from the outer corner of the eye and a
                horizontal line from the corner of the mouth, adjusted for each patient&apos;s specific facial proportions.
              </p>
              <p>
                The depth of the adhesion determines how prominent the dimple appears. Dr. Jain discusses the desired depth
                before surgery and calibrates accordingly. The goal is a dimple that is visible when you smile without
                creating a permanent indentation at rest, which is how natural dimples behave once the initial healing period
                passes.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #3D2566 0%, rgba(61, 37, 102, 0) 15%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function DimpleTypesSection() {
  const types = [
    {
      title: "Cheek Dimples",
      icon: Smile,
      copy: "The most commonly requested option. Created on one or both cheeks, positioned based on facial proportions and how your cheeks move when you smile.",
    },
    {
      title: "Bilateral Dimples",
      icon: Users,
      copy: "Symmetrical dimples on both cheeks. Placement on each side is marked and assessed independently to ensure the dimples appear balanced across different expressions.",
    },
    {
      title: "Unilateral Dimple",
      icon: CircleDot,
      copy: "A single dimple on one cheek for a distinctive, asymmetric look. Some patients prefer this for a more individual result.",
    },
    {
      title: "Chin Dimple",
      icon: ScanFace,
      copy: "A cleft chin appearance created through the same technique applied to the chin. Less common than cheek dimples and suits specific facial structures. Dr. Jain assesses whether your chin anatomy is appropriate for this during consultation.",
    },
  ];

  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="dimple-types-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Procedure Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Types of Dimples You Can Create</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {types.map((item, index) => (
            <div key={item.title} className="bg-white p-8 border border-[#E8E2D9]" data-testid={`dimple-type-card-${index}`}>
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <item.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{item.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{item.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowProcedurePerformedSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="how-procedure-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">The Procedure</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">How Dimple Creation Surgery is Performed</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              The procedure is performed under local anesthesia in a clinic setting. No hospital admission is required. Dr.
              Jain first marks the planned dimple position with the patient in a natural upright posture and smiling,
              confirming the location looks correct before proceeding.
            </p>
            <p>
              A small incision is made inside the cheek, against the inner mucosal surface. No external incision is made. A
              suture is placed to create an adhesion between the inner skin and the cheek muscle at the marked point. This
              adhesion creates the depression that forms the dimple. The entry point inside the mouth is closed and no
              external stitches are required.
            </p>
            <p>
              The total procedure takes 20 to 40 minutes. Patients experience mild swelling and minimal discomfort
              afterward. Normal oral hygiene and a soft diet for a few days are the main post-procedure requirements.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function RecoveryTimelineSection() {
  const milestones = [
    {
      period: "Days 1 to 3",
      description:
        "Mild swelling and tenderness inside the cheek. The dimple is visible even at rest during this period, which is normal and temporary.",
      icon: BedDouble,
    },
    {
      period: "Days 5 to 7",
      description:
        "Swelling reduces. The dimple begins to appear more naturally, showing primarily when smiling rather than at rest.",
      icon: Monitor,
    },
    {
      period: "Weeks 1 to 2",
      description:
        "Most patients resume full normal activity. Oral hygiene is important during this period. The dimple continues to settle.",
      icon: Dumbbell,
    },
    {
      period: "Weeks 3 to 4",
      description:
        "Final result is visible. The dimple behaves dynamically, appearing on smiling and relaxing at rest, consistent with a natural dimple.",
      icon: Sparkles,
    },
  ];

  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="recovery-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-12 text-center">Recovery Timeline</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-deep-navy/10" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-milestone-${index}`}>
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-deep-navy/40" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-warm-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-deep-navy/10" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-mobile-${index}`}>
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-deep-navy/40 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-warm-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const COMPARISON_ROWS = [
  {
    label: "Results Duration",
    surgery: "Permanent.",
    fillers: "6 to 12 months, requires repeat treatment.",
  },
  {
    label: "Appearance",
    surgery: "Natural, dynamic dimple that appears on smiling.",
    fillers: "Static indentation that does not behave like a natural dimple.",
  },
  {
    label: "Downtime",
    surgery: "1 to 2 days.",
    fillers: "Minimal but repeated.",
  },
  {
    label: "Cost Over Time",
    surgery: "One-time procedure.",
    fillers: "Ongoing cost for maintenance sessions.",
  },
  {
    label: "Reversibility",
    surgery: "Permanent, early reversal possible if required.",
    fillers: "Dissolves naturally or can be removed.",
  },
];

function SurgeryVsFillersSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="surgery-vs-fillers-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Understanding Your Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">
          Dimple Surgery vs. Non-Surgical Options
        </h2>
        <div className="max-w-5xl mx-auto">
          <div className="hidden md:grid md:grid-cols-3 gap-0 border border-[#E8E2D9] bg-[#E8E2D9] overflow-hidden">
            <div className="bg-premium-purple text-warm-white font-serif text-sm md:text-base p-4 font-medium" />
            <div className="bg-premium-purple text-warm-white font-serif text-sm md:text-base p-4 text-center">Surgery</div>
            <div className="bg-premium-purple text-warm-white font-serif text-sm md:text-base p-4 text-center">Fillers</div>
            {COMPARISON_ROWS.map((row) => (
              <React.Fragment key={row.label}>
                <div className="bg-white p-4 text-sm font-medium text-deep-navy border-t border-[#E8E2D9]">{row.label}</div>
                <div className="bg-white p-4 text-sm text-deep-navy/70 leading-relaxed border-t border-[#E8E2D9]">
                  {row.surgery}
                </div>
                <div className="bg-white p-4 text-sm text-deep-navy/70 leading-relaxed border-t border-[#E8E2D9]">
                  {row.fillers}
                </div>
              </React.Fragment>
            ))}
          </div>
          <div className="md:hidden space-y-4">
            {COMPARISON_ROWS.map((row) => (
              <div key={row.label} className="bg-white p-6 border border-[#E8E2D9]">
                <h3 className="font-serif text-lg text-premium-purple mb-4">{row.label}</h3>
                <div className="space-y-3 text-sm">
                  <div>
                    <p className="font-medium text-deep-navy mb-1">Surgery</p>
                    <p className="text-deep-navy/70 leading-relaxed">{row.surgery}</p>
                  </div>
                  <div>
                    <p className="font-medium text-deep-navy mb-1">Fillers</p>
                    <p className="text-deep-navy/70 leading-relaxed">{row.fillers}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-sm text-deep-navy/60 text-center mt-8 max-w-3xl mx-auto leading-relaxed">
            Fillers are not a standard technique for dimple creation. Surgery is the only method that replicates the natural
            anatomy of a dimple with a permanent, dynamic result.
          </p>
        </div>
      </div>
    </section>
  );
}

function WhoIsGoodCandidateSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="who-is-candidate-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Am I a Candidate?</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8 text-center">
            Who Is a Good Candidate for Dimpleplasty?
          </h2>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Dimpleplasty suits adults of any age who want to add dimples to their smile and have realistic expectations
              about the result. The procedure works across a wide range of facial structures and is suitable for both men
              and women. Patients do not need to have any specific facial concern beyond the desire for dimples.
            </p>
            <p>
              Patients with active oral infections or poor wound healing conditions should not undergo the procedure until
              those issues are resolved. Patients who want a dramatic facial transformation are better suited to other
              procedures. Dimpleplasty is a subtle enhancement. Its value is in adding a specific feature that improves how
              your smile reads, not in changing the overall structure of your face.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function PatientResultsSection() {
  return <PatientTestimonials procedureName="Dimpleplasty" />;
}

function FAQSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Common Questions</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {DIMPLEPLASTY_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`item-${index}`} className="border-b border-deep-navy/10">
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ConsultationCTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">
          Book Your Dimpleplasty Consultation
        </h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain assesses your facial proportions, smile dynamics, and cheek structure to
          determine the optimal dimple position and depth for your face. The procedure is planned precisely before surgery so
          the result looks natural from the first time you smile.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button
            asChild
            className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
            data-testid="cta-book-consultation"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Rhinoplasty",
      description: "Refine nasal shape alongside smile enhancement for complete facial balance.",
      href: "/rhinoplasty-in-mumbai",
    },
    {
      name: "Chin Augmentation",
      description: "Define the lower face to complement new dimples and smile aesthetics.",
      href: "/chin-augmentation-in-mumbai",
    },
    {
      name: "Lip Augmentation",
      description: "Enhance lip shape and volume alongside smile improvement.",
      href: "/botox-treatment-mumbai",
    },
  ];

  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Related Procedures</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Related Procedures</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function DimpleplastyPage() {
  return (
    <>
      <SEO
        title="Dimple Creation Surgery | Dr. Vicky Jain"
        description="Create natural-looking dimples with dimpleplasty. Dr. Vicky Jain performs the procedure in 20 to 40 minutes with no visible scars. Book your consultation."
        canonical="/dimple-creation-surgery-mumbai"
      />
      <DimpleplastyJsonLd />
      <Breadcrumb />
      <HeroSection />
      <WhatIsSection />
      <DrJainApproachSection />
      <DimpleTypesSection />
      <HowProcedurePerformedSection />
      <RecoveryTimelineSection />
      <SurgeryVsFillersSection />
      <WhoIsGoodCandidateSection />
      <PatientResultsSection />
      <FAQSection />
      <ConsultationCTASection />
      <RelatedProceduresSection />
    </>
  );
}
